/**
 * AD-195 virtual race clock — the single formula every surface shares.
 *
 * A race in simulation mode has one SimulationState (backed by the
 * race_simulations table). The simulated "now" is DERIVED from it:
 *
 *   sim_now = playing ? anchor_sim_time + (wallNow − anchor_wall_time) × speed
 *                     : anchor_sim_time
 *
 * There is deliberately NO ticking timer anywhere: play/pause/speed/teleport
 * are each just a re-anchored row write, and every consumer (Express, the
 * Postgres sim_now() function, each client UI) computes the identical time
 * from the same four values. Postgres mirror: public.sim_now(race_id) in
 * supabase/migrations/20260803190000_ad195_race_simulations.sql.
 */

export interface SimulationState {
  /** Position on the race's own event timeline (ISO timestamptz). */
  anchor_sim_time: string;
  /** Real-world moment the anchor was set, written by the SERVER (ISO). */
  anchor_wall_time: string;
  /** Playback multiplier, 0.5–600. */
  speed: number;
  playing: boolean;
}

export function computeSimNow(state: SimulationState, wallNow: Date = new Date()): Date {
  const anchorSim = new Date(state.anchor_sim_time).getTime();
  if (!state.playing) return new Date(anchorSim);
  const anchorWall = new Date(state.anchor_wall_time).getTime();
  // Clamp: a client whose clock is slightly behind the server's anchor must
  // not compute a sim time BEFORE the anchor — that would make the clock
  // appear to jump backward right after pressing play.
  const elapsed = Math.max(0, wallNow.getTime() - anchorWall);
  return new Date(anchorSim + elapsed * state.speed);
}

/** "14h 32m 05s" — the Trackleaders-style race-clock readout. */
export function formatRaceClock(elapsedMs: number): string {
  if (elapsedMs < 0) return '-' + formatRaceClock(-elapsedMs);
  const totalSeconds = Math.floor(elapsedMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${hours}h ${String(minutes).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`;
}

/**
 * Parse a human race-clock entry into elapsed ms. Accepts "14:32:00",
 * "14:32", "14h 32m", "14h 32m 05s", "90m". Returns null when unparseable —
 * callers show a validation message instead of guessing.
 */
export function parseRaceClock(text: string): number | null {
  const t = text.trim().toLowerCase();
  const colon = t.match(/^(\d{1,3}):(\d{1,2})(?::(\d{1,2}))?$/);
  if (colon) {
    return ((+(colon[1] ?? 0)) * 3600 + (+(colon[2] ?? 0)) * 60 + (+(colon[3] ?? 0))) * 1000;
  }
  const units = t.match(/^(?:(\d+)\s*h)?\s*(?:(\d+)\s*m)?\s*(?:(\d+)\s*s)?$/);
  if (units && (units[1] || units[2] || units[3])) {
    return ((+(units[1] ?? 0)) * 3600 + (+(units[2] ?? 0)) * 60 + (+(units[3] ?? 0))) * 1000;
  }
  return null;
}
